import ApiWithLogging from '../utils/apiWithLogging';

// ข้อมูลบริษัทแบบย่อ
export interface CompanyBasic {
  id: number;
  code: string;
  name: string;
}

// ข้อมูลผู้ใช้แบบย่อ
export interface UserBasic {
  id: number;
  username: string;
  full_name?: string;
}

// ประเภทข้อมูลผู้มีอำนาจลงนาม
export interface AuthorizedSigner {
  id: number;
  company_id: number;
  full_name: string;
  name?: string; // alias of full_name (legacy)
  position: string;
  email?: string;
  phone?: string;
  is_active: boolean;
  is_primary?: boolean;
  created_at: string;
  updated_at?: string;
  created_by_user_id?: number;
  updated_by_user_id?: number;
  // Relations
  company?: CompanyBasic;
  created_by?: UserBasic;
  updated_by?: UserBasic;
}

// ข้อมูลสำหรับการสร้างหรือแก้ไขผู้มีอำนาจลงนาม
export interface AuthorizedSignerForm {
  company_id: number;
  full_name: string;
  name?: string; // alias of full_name (legacy)
  position: string;
  email?: string;
  phone?: string;
  is_active?: boolean;
  is_primary?: boolean;
}

// พารามิเตอร์สำหรับการดึงข้อมูล
export interface GetAuthorizedSignersParams {
  page?: number;
  limit?: number;
  company_id?: number;
  is_active?: boolean;
  search?: string;
}

// การตอบกลับจากการดึงข้อมูล
export interface GetAuthorizedSignersResponse {
  items: AuthorizedSigner[];
  total: number;
  page: number;
  limit: number;
}

// บริการจัดการข้อมูลผู้มีอำนาจลงนาม
const authorizedSignerService = {
  // ดึงรายการผู้มีอำนาจลงนามทั้งหมด
  async getAuthorizedSigners(params: GetAuthorizedSignersParams = {}): Promise<GetAuthorizedSignersResponse> {
    const queryParams: any = {};
    
    if (params.page !== undefined) queryParams.page = params.page;
    if (params.limit !== undefined) queryParams.limit = params.limit;
    if (params.company_id !== undefined) queryParams.company_id = params.company_id;
    if (params.is_active !== undefined) queryParams.is_active = params.is_active;
    if (params.search !== undefined) queryParams.search = params.search;
    
    // ตั้งค่า default
    if (!queryParams.page) queryParams.page = 1;
    if (!queryParams.limit) queryParams.limit = 10;
    
    const response = await ApiWithLogging.get<GetAuthorizedSignersResponse>('/authorized-signers/', 
      { params: queryParams },
      {
        service: 'authorizedSignerService',
        action: 'view_authorized_signers_list',
        entityType: 'authorized_signer'
      }
    );
    
    return response;
  },

  // ดึงข้อมูลผู้มีอำนาจลงนามตาม id
  async getAuthorizedSigner(id: number): Promise<AuthorizedSigner> {
    const response = await ApiWithLogging.get<AuthorizedSigner>(`/authorized-signers/${id}`, {},
      {
        service: 'authorizedSignerService',
        action: 'view_authorized_signer_detail',
        entityType: 'authorized_signer',
        entityId: id
      }
    );
    return response;
  },

  // สร้างผู้มีอำนาจลงนามใหม่
  async createAuthorizedSigner(data: AuthorizedSignerForm): Promise<AuthorizedSigner> {
    const response = await ApiWithLogging.post<AuthorizedSigner>('/authorized-signers/', data, {},
      {
        service: 'authorizedSignerService',
        action: 'create_authorized_signer',
        entityType: 'authorized_signer',
        details: {
          company_id: data.company_id,
          full_name: data.full_name,
          position: data.position
        }
      }
    );
    return response;
  },

  // แก้ไขข้อมูลผู้มีอำนาจลงนาม
  // BUG-S162-P1: ส่ง updated_at ที่โหลดมาเป็น ifMatch → backend ตรวจ optimistic lock (stale → 409)
  async updateAuthorizedSigner(id: number, data: Partial<AuthorizedSignerForm>, ifMatch?: string): Promise<AuthorizedSigner> {
    const response = await ApiWithLogging.put<AuthorizedSigner>(`/authorized-signers/${id}`, data, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
      {
        service: 'authorizedSignerService',
        action: 'update_authorized_signer',
        entityType: 'authorized_signer',
        entityId: id,
        details: data
      }
    );
    return response;
  },

  // ลบผู้มีอำนาจลงนาม
  async deleteAuthorizedSigner(id: number): Promise<any> {
    const response = await ApiWithLogging.delete(`/authorized-signers/${id}`, {},
      {
        service: 'authorizedSignerService',
        action: 'delete_authorized_signer',
        entityType: 'authorized_signer',
        entityId: id
      }
    );
    return response;
  },

  // ดึงผู้มีอำนาจลงนามที่ active ของบริษัท
  async getActiveSignersByCompany(companyId: number): Promise<AuthorizedSigner[]> {
    const response = await ApiWithLogging.get<AuthorizedSigner[]>(
      `/authorized-signers/company/${companyId}/active`, {},
      {
        service: 'authorizedSignerService',
        action: 'view_active_signers_by_company',
        entityType: 'authorized_signer',
        details: { company_id: companyId }
      }
    );
    return response;
  },

  // ส่งออกข้อมูลผู้มีอำนาจลงนามเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้กรองแล้วกดส่งออก
  // จะได้ไฟล์ทั้งระบบโดยไม่มีอะไรเตือน (ไฟล์ที่ได้ต้องตรงกับที่เห็นบนจอ)
  async exportToExcel(params?: { search?: string; is_active?: boolean; company_id?: string | number }): Promise<Blob> {
    const query: Record<string, string> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = String(params.is_active);
    if (params?.company_id) query.company_id = String(params.company_id);

    const response = await ApiWithLogging.get('/authorized-signers/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'authorizedSignerService',
        action: 'export_authorized_signers_excel',
        entityType: 'authorized_signer'
      }
    );
    return response;
  }
};

export default authorizedSignerService;